/**
 * The visit's consumables tab and the shelf (Boona storage plan ST8):
 * "left after this" per line, whether a service's card is already written
 * off, and what the materials are against the visit's price.
 *
 * `stock` comes from GET /api/v1/technological_cards/record_context/{id}:
 * "good:storage" → { on_hand, minimum, unit } — the shelf as it is now, so a
 * line already saved on this visit has already left it.
 */

export interface ShelfStock {
  on_hand: number;
  minimum: number | null;
  unit: string;
}

export interface StockLine {
  id: number;
  good_id: number;
  storage_id: number;
  amount: number;
  /** what this line had when the visit was opened (0 for a new line) */
  recordedAmount?: number;
  service_id?: number;
  technological_card_id?: number;
}

export type LeftTone = "below_zero" | "low" | "ok";

export function stockKey(goodId: number, storageId: number): string {
  return `${goodId}:${storageId}`;
}

/**
 * What would be left on the shelf of each line's good once this visit is
 * saved: today's on hand less what the visit adds or takes back since it was
 * opened (every line of that good in that warehouse counts). Lines whose
 * shelf is unknown are left out.
 */
export function leftAfter(
  lines: StockLine[],
  stock: Record<string, ShelfStock> | null | undefined,
): Record<number, { left: number; unit: string; tone: LeftTone }> {
  const out: Record<number, { left: number; unit: string; tone: LeftTone }> = {};
  if (!stock) return out;
  const change: Record<string, number> = {};
  for (const l of lines) {
    const key = stockKey(l.good_id, l.storage_id);
    change[key] = (change[key] ?? 0) + (Number(l.amount) || 0) - (Number(l.recordedAmount) || 0);
  }
  for (const l of lines) {
    const s = stock[stockKey(l.good_id, l.storage_id)];
    if (!s) continue;
    const left = Math.round((s.on_hand - (change[stockKey(l.good_id, l.storage_id)] ?? 0)) * 1000) / 1000;
    const tone: LeftTone = left < 0 ? "below_zero" : s.minimum != null && left <= s.minimum ? "low" : "ok";
    out[l.id] = { left, unit: s.unit, tone };
  }
  return out;
}

/** True when a line of this service came from this card already. */
export function cardWrittenOff(lines: StockLine[], serviceId: number, cardId: number): boolean {
  return lines.some((l) => l.service_id === serviceId && l.technological_card_id === cardId);
}

/** The visit's price: each service's actual price (or its catalogue price). */
export function visitPrice(services: { cost?: number | null; price?: number | null; first_cost?: number | null }[]): number {
  return (services ?? []).reduce((sum, s) => sum + Number(s.cost ?? s.price ?? s.first_cost ?? 0), 0);
}

/** Materials as a share of the visit's price, a whole percent; null without a price. */
export function materialsShare(materials: number, price: number): number | null {
  return price > 0 ? Math.round((materials / price) * 100) : null;
}
